import type { Hex } from 'viem'
import * as z from 'zod'

// Native replacement for lodash/isNil - checks for both null and undefined
const isNil = (value: unknown): value is null | undefined => value == null
import type {
  SacrificeStatsResponse,
  UnpricedSacrificesResponse,
  NetworkDetails,
  RecentSacrificesResponse,
  SacrificeInfo,
  SacrificeQueryFilters,
  MarketsResponse,
  MarketPriceResult,
  LeaderboardResponse,
  SupportedProjectNetworkAndTimestamp,
} from 'sacrifice-utils/types'
import {
  type HealthCheckResponse,
  type ProjectsResponse,
  type AdminProjectResponse,
  type ValidateRequest,
  type ValidateResponse,
  type RetrieveStatsRequest,
  type ReportSubmissionRequest,
  type ReportSubmissionResponse,
  type ReferralSubmissionInputs,
  type ReferralSubmissionResponse,
  type ReferralListResponse,
  type NetworkManagementInputs,
  type NetworkManagementResponse,
  type TokenManagementInputs,
  type TokenManagementResponse,
  type AdminManagementInputs,
  type AdminManagementResponse,
  type SacrificeMutationUpsertInputs,
  type ReportToggleInputs,
  type ProjectUpdateInputs,
  type ProjectUpdateResponse,
  type LatestBlockRequest,
  type LatestBlockResponse,
  type AdminProjectRequest,
  type AdminProjectsResponse,
  type MarketPricePair,
  type AdminReferralsResponse,
  type AdminReportsResponse,
  type AdminSacrificeMutationsResponse,
  type AdminPriceOverridesResponse,
  type ReportToggleResponse,
  type SacrificeDetailsResponse,
  type SacrificeMutationUpsertResponse,
  type ProgressResponse,
  type LeaderboardOrderBy,
  type DirectoryStatsResponse,
  type GlobalNetwork,
  type PendingRegistration,
  type InstanceAuthRequest,
  type InstanceWhoAmIResponse,
  type IdentityLabelListResponse,
  type IdentityLabelMutationRequest,
  type InstanceActivityRequest,
  type InstanceActivityResponse,
  type InstanceCampaignsRequest,
  type InstanceCampaignsResponse,
  type InstanceIdentitiesRequest,
  type InstanceIdentitiesResponse,
  type InstanceCycleStatusRequest,
  type InstanceCycleStatusResponse,
  type LabelFilter,
  IdentityLabelMutationRequestSchema,
  InstanceActivityRequestSchema,
  InstanceCampaignsRequestSchema,
  InstanceIdentitiesRequestSchema,
  InstanceCycleStatusRequestSchema,
  ValidateRequestSchema,
  GetAddressesRequestSchema,
  GenerateAddressRequestSchema,
  ReportSubmissionRequestSchema,
  ReferralSubmissionRequestSchema,
  NetworkManagementRequestSchema,
  TokenManagementRequestSchema,
  AdminManagementRequestSchema,
  SacrificeMutationUpsertRequestSchema,
  ReportToggleRequestSchema,
  ProjectUpdateRequestSchema,
  LatestBlockRequestSchema,
  AdminProjectRequestSchema,
  RetrieveStatsRequestSchema,
  MarketPriceRequestSchema,
  PendingRegistrationSchema,
  InstanceAuthRequestSchema,
  serializeRequest,
  serializeInputs,
} from 'sacrifice-utils/api'
import { ProjectNetworkIdentifier } from '@provex/eip712-utils/keys'
import { CampaignImportRequestSchema, type CampaignConfig } from 'sacrifice-utils/campaign-config'
import type {
  PoolUploadBeginInputs,
  PoolUploadBeginResponse,
  PoolUploadChunkResponse,
  PoolUploadCommitResponse,
} from '../wizard/upload-client'
import type { AddressSetEntry } from '@votum/addresses/browser'

// Environment configuration
const SACRIFICE_INDEXER_URL = import.meta.env.VITE_SACRIFICE_INDEXER_URL || location.origin

// Custom error class for API errors
export class SacrificeApiError extends Error {
  constructor(
    message: string,
    public status?: number,
    public response?: Response
  ) {
    super(message)
    this.name = 'SacrificeApiError'
  }
}

// Generic wrapper for serializeRequest that handles bigint serialization in inputs
function serializeRequestWithInputs<T extends z.ZodTypeAny>(schema: T, data: z.infer<T>): string {
  // If data has an 'inputs' property, serialize its bigints
  const dataObj = data as Record<string, unknown>
  const processedData =
    'inputs' in dataObj && typeof dataObj.inputs === 'object' && dataObj.inputs !== null
      ? { ...dataObj, inputs: serializeInputs(dataObj.inputs as Record<string, unknown>) }
      : dataObj

  return serializeRequest(schema, processedData as z.infer<T>)
}

/**
 * Discriminated result returned by {@link sacrificeApi.addresses.generate}.
 *
 * - `kind: 'addresses'` — the registry has already assigned an index; the
 *   addresses array is ready to display.
 * - `kind: 'pending'`   — the registry has not yet assigned an index (HTTP 202).
 *   The `pending` payload describes the relay job status and the registry
 *   contract so the client can show both "have us submit it" (voucher) and
 *   "submit it yourself" (registerSelf) doors.
 */
export type GenerateAddressResult =
  | { kind: 'addresses'; addresses: SacrificeInfo[] }
  | { kind: 'pending'; pending: PendingRegistration }

// Generic fetch wrapper with error handling and retry logic for 503 errors
const doFetch = async <T>({
  endpoint,
  options,
  retryCount = 0,
}: {
  endpoint: string
  options: RequestInit
  retryCount?: number
}): Promise<T> => {
  const MAX_RETRIES = 5
  const RETRY_DELAY_MS = 1000 // Start with 1 second

  const url = `${SACRIFICE_INDEXER_URL}${endpoint}`

  try {
    const response = await fetch(url, {
      mode: 'cors',
      // credentials: 'omit', // Don't send credentials for cross-origin requests
      credentials: location.protocol === 'https:' ? 'include' : 'omit',
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    })

    if (!response.ok) {
      let errorMessage = `HTTP ${response.status}: ${response.statusText}`

      try {
        const errorData = await response.json()
        if (errorData.message) {
          errorMessage = errorData.message
        }
      } catch {
        // If we can't parse the error response, use the default message
      }

      // Retry logic for 503 Service Unavailable
      if (response.status === 503 && retryCount < MAX_RETRIES) {
        const count = retryCount + 1
        const delay = RETRY_DELAY_MS * count // Exponential backoff
        console.warn(
          `Service unavailable (503), retrying in ${delay}ms... (attempt ${count}/${MAX_RETRIES})`
        )

        await new Promise(resolve => setTimeout(resolve, delay))
        return doFetch<T>({ endpoint, options, retryCount: count })
      }

      throw new SacrificeApiError(errorMessage, response.status, response)
    }

    return (await response.json()) as T
  } catch (error) {
    // Re-throw abort errors as-is so React Query can handle them properly
    if (error instanceof Error && (error.name === 'AbortError' || error.name === 'AbortedError')) {
      throw error
    }
    // Re-throw SacrificeApiError as-is
    if (error instanceof SacrificeApiError) {
      throw error
    }
    // Wrap network errors and other errors
    throw new SacrificeApiError(
      error instanceof Error ? error.message : 'Network error',
      0,
      undefined
    )
  }
}

/**
 * Like doFetch but returns the raw HTTP status alongside the parsed body.
 * Used only for endpoints that use 202 as a semantic (not an error) status.
 */
const doFetchRaw = async ({
  endpoint,
  options,
}: {
  endpoint: string
  options: RequestInit
}): Promise<{ status: number; body: unknown }> => {
  const url = `${SACRIFICE_INDEXER_URL}${endpoint}`
  let response: Response

  try {
    response = await fetch(url, {
      mode: 'cors',
      credentials: location.protocol === 'https:' ? 'include' : 'omit',
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    })
  } catch (error) {
    throw new SacrificeApiError(
      error instanceof Error ? error.message : 'Network error',
      0,
      undefined
    )
  }

  // 200 and 202 are success statuses; anything else is an error.
  if (!response.ok && response.status !== 202) {
    let errorMessage = `HTTP ${response.status}: ${response.statusText}`
    try {
      const errorData = (await response.json()) as Record<string, unknown>
      if (typeof errorData.message === 'string') {
        errorMessage = errorData.message
      }
    } catch {
      // Use the default message when the body is not JSON.
    }
    throw new SacrificeApiError(errorMessage, response.status, response)
  }

  const body = await response.json()
  return { status: response.status, body }
}

// Main API client
export const sacrificeApi = {
  // Health check endpoint
  health: async ({ signal }: { signal: AbortSignal | undefined }) => {
    return await doFetch<HealthCheckResponse>({ endpoint: '/api/v1/health', options: { signal } })
  },

  // Progress endpoint - get indexing progress for all networks in a project
  progress: async ({
    projectId,
    signal,
  }: {
    projectId: string
    signal: AbortSignal | undefined
  }) => {
    const params = new URLSearchParams()
    params.append('projectId', projectId)
    const queryString = params.toString()
    return await doFetch<ProgressResponse>({
      endpoint: `/api/v1/progress?${queryString}`,
      options: { signal },
    })
  },

  // Get sacrifice statistics grouped by network
  stats: {
    publicSacrificeData: async ({
      request,
      signal,
    }: {
      request: RetrieveStatsRequest
      signal: AbortSignal | undefined
    }) => {
      const path = '/api/v1/stats/sacrifices'
      return await doFetch<SacrificeStatsResponse>({
        endpoint: path,
        options: {
          method: 'POST',
          body: serializeRequestWithInputs(RetrieveStatsRequestSchema, request),
          signal,
        },
      })
    },
  },

  // Get unpriced sacrifices for a network
  networks: {
    getAll: async ({ signal }: { signal: AbortSignal | undefined }) => {
      return await doFetch<GlobalNetwork[]>({
        endpoint: '/api/v1/networks',
        options: { signal },
      })
    },
    getUnpriced: async ({
      limit,
      projectId,
      signal,
    }: {
      limit?: number
      projectId?: string | null
      signal: AbortSignal | undefined
    }) => {
      const params = new URLSearchParams()
      if (!isNil(projectId)) params.append('projectId', projectId!)
      if (limit !== undefined) params.append('limit', limit.toString())
      const queryString = params.toString()
      const queryParams = queryString ? `?${queryString}` : ''
      return await doFetch<UnpricedSacrificesResponse>({
        endpoint: `/api/v1/unpriced${queryParams}`,
        options: { signal },
      })
    },
    getRecent: async ({
      projectId,
      networkType,
      chainId,
      limit,
      offset,
      signal,
    }: ProjectNetworkIdentifier & {
      limit?: number
      offset?: number
      signal: AbortSignal | undefined
    }) => {
      const params = new URLSearchParams()
      params.append('projectId', projectId)
      params.append('networkType', networkType)
      params.append('chainId', chainId.toString())
      if (limit !== undefined) params.append('limit', limit.toString())
      if (offset !== undefined) params.append('offset', offset.toString())
      const queryString = params.toString()
      const queryParams = queryString ? `?${queryString}` : ''
      return await doFetch<RecentSacrificesResponse>({
        endpoint: `/api/v1/networks/${networkType}/${chainId}/recent${queryParams}`,
        options: { signal },
      })
    },
    get: async ({
      projectId,
      networkType,
      chainId,
      signal,
    }: {
      projectId: string
      networkType: string
      chainId: number
      signal: AbortSignal | undefined
    }) => {
      return await doFetch<NetworkDetails>({
        endpoint: `/api/v1/networks/${projectId}/${networkType}/${chainId}`,
        options: { signal },
      })
    },
  },

  // Global (scoped to project id) sacrifices endpoints
  sacrifices: {
    getRecent: async ({
      limit,
      offset,
      projectId,
      signal,
    }: {
      limit?: number
      offset?: number
      projectId?: string | null
      signal: AbortSignal | undefined
    }) => {
      const params = new URLSearchParams()
      if (!isNil(projectId)) params.append('projectId', projectId!)
      if (limit !== undefined) params.append('limit', limit.toString())
      if (offset !== undefined) params.append('offset', offset.toString())
      const queryString = params.toString()
      const queryParams = queryString ? `?${queryString}` : ''
      return await doFetch<RecentSacrificesResponse>({
        endpoint: `/api/v1/sacrifices/recent${queryParams}`,
        options: { signal },
      })
    },
    // General query endpoint that supports multiple filters
    query: async ({
      filters,
      signal,
    }: {
      filters: SacrificeQueryFilters
      signal: AbortSignal | undefined
    }) => {
      const params = new URLSearchParams()
      if (!isNil(filters.networkType)) params.append('networkType', filters.networkType!)
      if (!isNil(filters.chainId)) params.append('chainId', filters.chainId!.toString())
      if (!isNil(filters.pointsDestinationAddress)) {
        params.append('pointsDestinationAddress', filters.pointsDestinationAddress)
      }
      if (!isNil(filters.tokenAddress)) params.append('tokenAddress', filters.tokenAddress!)
      if (filters.limit !== undefined) params.append('limit', filters.limit.toString())
      if (filters.offset !== undefined) params.append('offset', filters.offset.toString())
      if (filters.orderBy) params.append('orderBy', filters.orderBy)
      if (filters.orderDirection) params.append('orderDirection', filters.orderDirection)
      const queryString = params.toString()
      const queryParams = queryString ? `?${queryString}` : ''
      return await doFetch<RecentSacrificesResponse>({
        endpoint: `/api/v1/${filters.projectId}/sacrifices${queryParams}`,
        options: { signal },
      })
    },
  },

  // Points endpoints
  points: {
    // Get points leaderboard
    leaderboard: async ({
      projectId,
      version,
      limit,
      offset,
      orderDirection = 'desc',
      orderBy,
      signal,
    }: {
      projectId: string
      version?: string
      limit?: number
      offset?: number
      orderDirection?: 'asc' | 'desc'
      orderBy?: LeaderboardOrderBy
      signal: AbortSignal | undefined
    }) => {
      const params = new URLSearchParams()
      if (version) params.append('version', version)
      if (limit !== undefined) params.append('limit', limit.toString())
      if (offset !== undefined) params.append('offset', offset.toString())
      if (orderDirection) params.append('orderDirection', orderDirection)
      if (orderBy) params.append('orderBy', orderBy)
      const queryString = params.toString()
      const queryParams = queryString ? `?${queryString}` : ''
      return await doFetch<LeaderboardResponse>({
        endpoint: `/api/v1/${projectId}/leaderboard${queryParams}`,
        options: { signal },
      })
    },
  },

  // Address validation and generation
  addresses: {
    all: async ({
      inputs,
      projectId,
      signature,
      signal,
    }: {
      inputs: ValidateRequest['inputs']
      projectId: string
      signature: Hex
      signal: AbortSignal | undefined
    }) => {
      return await doFetch<ValidateResponse>({
        endpoint: '/api/v1/address/all',
        options: {
          method: 'POST',
          body: serializeRequestWithInputs(GetAddressesRequestSchema, {
            signature,
            inputs,
            projectId,
          }),
          signal,
        },
      })
    },

    generate: async ({
      inputs,
      signature,
      signal,
    }: {
      inputs: SupportedProjectNetworkAndTimestamp
      signature: Hex
      signal: AbortSignal | undefined
    }): Promise<GenerateAddressResult> => {
      const { status, body } = await doFetchRaw({
        endpoint: '/api/v1/address',
        options: {
          method: 'POST',
          body: serializeRequestWithInputs(GenerateAddressRequestSchema, { signature, inputs }),
          signal,
        },
      })

      if (status === 202) {
        const pending = PendingRegistrationSchema.parse(body)
        return { kind: 'pending', pending }
      }

      // 200: the registry has assigned an index — body is SacrificeInfo[].
      return { kind: 'addresses', addresses: body as SacrificeInfo[] }
    },

    registerVoucher: async ({
      projectId,
      networkType,
      chainId,
      destination,
      deadline,
      signature,
      signal,
    }: {
      projectId: string
      networkType: string
      chainId: bigint
      destination: string
      deadline: bigint
      signature: string
      signal: AbortSignal | undefined
    }) => {
      return await doFetch<{ relayJob: { status: string; txHash: string | null } }>({
        endpoint: '/api/v1/address/register-voucher',
        options: {
          method: 'POST',
          body: JSON.stringify({
            projectId,
            networkType,
            chainId: { __type__: 'bigint', value: chainId.toString() },
            destination,
            deadline: { __type__: 'bigint', value: deadline.toString() },
            signature,
          }),
          signal,
        },
      })
    },
  },

  reports: {
    submit: async ({
      reportData,
      signal,
    }: {
      reportData: ReportSubmissionRequest
      signal: AbortSignal | undefined
    }) => {
      return await doFetch<ReportSubmissionResponse>({
        endpoint: '/api/v1/reports',
        options: {
          method: 'POST',
          body: serializeRequestWithInputs(ReportSubmissionRequestSchema, reportData),
          signal,
        },
      })
    },
  },

  // Projects endpoints
  projects: {
    getAll: async ({ signal }: { signal: AbortSignal | undefined }) => {
      return await doFetch<ProjectsResponse>({
        endpoint: '/api/v1/projects',
        options: { signal },
      })
    },
    getNetworks: async ({
      projectId,
      signal,
    }: {
      projectId: string
      signal: AbortSignal | undefined
    }) => {
      return await doFetch<NetworkDetails[]>({
        endpoint: `/api/v1/projects/${projectId}/networks`,
        options: { signal },
      })
    },
  },

  // Directory endpoints
  directory: {
    getStats: async ({ signal }: { signal: AbortSignal | undefined }) => {
      return await doFetch<DirectoryStatsResponse>({
        endpoint: '/api/v1/directory/stats',
        options: { signal },
      })
    },
  },

  // Admin endpoints
  admin: {
    getProjects: async ({
      request,
      signal,
    }: {
      request: ValidateRequest
      signal: AbortSignal | undefined
    }) => {
      return await doFetch<AdminProjectsResponse>({
        endpoint: '/api/v1/admin/projects',
        options: {
          method: 'POST',
          body: serializeRequestWithInputs(ValidateRequestSchema, request),
          signal,
        },
      })
    },
    getProject: async ({
      request,
      signal,
    }: {
      request: AdminProjectRequest
      signal: AbortSignal | undefined
    }) => {
      return await doFetch<AdminProjectResponse>({
        endpoint: '/api/v1/admin/project',
        options: {
          method: 'POST',
          body: serializeRequestWithInputs(AdminProjectRequestSchema, request),
          signal,
        },
      })
    },
    manageNetwork: async ({
      signature,
      inputs,
      signal,
    }: {
      signature: Hex
      inputs: NetworkManagementInputs
      signal: AbortSignal | undefined
    }) => {
      return await doFetch<NetworkManagementResponse>({
        endpoint: '/api/v1/admin/network',
        options: {
          method: 'POST',
          body: serializeRequestWithInputs(NetworkManagementRequestSchema, { signature, inputs }),
          signal,
        },
      })
    },
    manageAdmin: async ({
      signature,
      inputs,
      signal,
    }: {
      signature: Hex
      inputs: AdminManagementInputs
      signal: AbortSignal | undefined
    }): Promise<AdminManagementResponse> => {
      return await doFetch<AdminManagementResponse>({
        endpoint: '/api/v1/admin/admin',
        options: {
          method: 'POST',
          body: serializeRequestWithInputs(AdminManagementRequestSchema, { signature, inputs }),
          signal,
        },
      })
    },
    getLatestBlock: async ({
      request,
      signal,
    }: {
      request: LatestBlockRequest
      signal: AbortSignal | undefined
    }) => {
      return await doFetch<LatestBlockResponse>({
        endpoint: '/api/v1/admin/latest-block',
        options: {
          method: 'POST',
          body: serializeRequestWithInputs(LatestBlockRequestSchema, request),
          signal,
        },
      })
    },
    manageToken: async ({
      signature,
      inputs,
      signal,
    }: {
      signature: Hex
      inputs: TokenManagementInputs
      signal: AbortSignal | undefined
    }) => {
      return await doFetch<TokenManagementResponse>({
        endpoint: '/api/v1/admin/token',
        options: {
          method: 'POST',
          body: serializeRequestWithInputs(TokenManagementRequestSchema, { signature, inputs }),
          signal,
        },
      })
    },
    updateProject: async ({
      signature,
      inputs,
      signal,
    }: {
      signature: Hex
      inputs: ProjectUpdateInputs
      signal: AbortSignal | undefined
    }) => {
      return await doFetch<ProjectUpdateResponse>({
        endpoint: '/api/v1/admin/project/update',
        options: {
          method: 'POST',
          body: serializeRequestWithInputs(ProjectUpdateRequestSchema, { signature, inputs }),
          signal,
        },
      })
    },
    importCampaign: async ({
      signature,
      inputs,
      config,
      signal,
    }: {
      signature: Hex
      inputs: { timestamp: bigint }
      config: CampaignConfig
      signal: AbortSignal | undefined
    }) => {
      return await doFetch<{ projectId: string }>({
        endpoint: '/api/v1/admin/campaign/import',
        options: {
          method: 'POST',
          body: serializeRequestWithInputs(CampaignImportRequestSchema, {
            signature,
            inputs,
            config,
          }),
          signal,
        },
      })
    },
    exportCampaign: async ({
      request,
      signal,
    }: {
      request: AdminProjectRequest
      signal: AbortSignal | undefined
    }) => {
      return await doFetch<CampaignConfig>({
        endpoint: '/api/v1/admin/campaign/export',
        options: {
          method: 'POST',
          body: serializeRequestWithInputs(AdminProjectRequestSchema, request),
          signal,
        },
      })
    },
    getReferrals: async ({
      request,
      signal,
    }: {
      request: AdminProjectRequest
      signal: AbortSignal | undefined
    }) => {
      return await doFetch<AdminReferralsResponse>({
        endpoint: '/api/v1/admin/referrals',
        options: {
          method: 'POST',
          body: serializeRequestWithInputs(AdminProjectRequestSchema, request),
          signal,
        },
      })
    },
    getSacrificeMutations: async ({
      request,
      signal,
    }: {
      request: AdminProjectRequest
      signal: AbortSignal | undefined
    }) => {
      return await doFetch<AdminSacrificeMutationsResponse>({
        endpoint: '/api/v1/admin/sacrifice-mutations',
        options: {
          method: 'POST',
          body: serializeRequestWithInputs(AdminProjectRequestSchema, request),
          signal,
        },
      })
    },
    getPriceOverrides: async ({
      request,
      signal,
    }: {
      request: AdminProjectRequest
      signal: AbortSignal | undefined
    }) => {
      return await doFetch<AdminPriceOverridesResponse>({
        endpoint: '/api/v1/admin/price-overrides',
        options: {
          method: 'POST',
          body: serializeRequestWithInputs(AdminProjectRequestSchema, request),
          signal,
        },
      })
    },
    getReports: async ({
      request,
      signal,
    }: {
      request: AdminProjectRequest
      signal: AbortSignal | undefined
    }) => {
      return await doFetch<AdminReportsResponse>({
        endpoint: '/api/v1/admin/reports',
        options: {
          method: 'POST',
          body: serializeRequestWithInputs(AdminProjectRequestSchema, request),
          signal,
        },
      })
    },
    toggleReportIgnored: async ({
      signature,
      inputs,
      signal,
    }: {
      // A dedicated ReportToggle signature over these exact inputs — the login
      // signature is not accepted by this endpoint.
      signature: Hex
      inputs: ReportToggleInputs
      signal: AbortSignal | undefined
    }) => {
      return await doFetch<ReportToggleResponse>({
        endpoint: '/api/v1/admin/report/toggle-ignored',
        options: {
          method: 'POST',
          body: serializeRequestWithInputs(ReportToggleRequestSchema, { signature, inputs }),
          signal,
        },
      })
    },
    getSacrificeDetails: async ({
      request,
      signal,
    }: {
      request: AdminProjectRequest & {
        networkType: string
        chainId: number
        blockHash: string
        transactionHash: string
        tokenAddress: string
        logIndex: number
      }
      signal: AbortSignal | undefined
    }) => {
      return await doFetch<SacrificeDetailsResponse>({
        endpoint: '/api/v1/admin/sacrifice-details',
        options: {
          method: 'POST',
          body: JSON.stringify({
            signature: request.signature,
            inputs: serializeInputs(request.inputs),
            projectId: request.projectId,
            networkType: request.networkType,
            chainId: request.chainId,
            blockHash: request.blockHash,
            transactionHash: request.transactionHash,
            tokenAddress: request.tokenAddress,
            logIndex: request.logIndex,
          }),
          signal,
        },
      })
    },
    upsertSacrificeMutation: async ({
      signature,
      inputs,
      signal,
    }: {
      // A dedicated SacrificeMutation signature over the full mutation payload —
      // the login signature is not accepted by this endpoint.
      signature: Hex
      inputs: SacrificeMutationUpsertInputs
      signal: AbortSignal | undefined
    }) => {
      return await doFetch<SacrificeMutationUpsertResponse>({
        endpoint: '/api/v1/admin/sacrifice-mutation/upsert',
        options: {
          method: 'POST',
          body: serializeRequestWithInputs(SacrificeMutationUpsertRequestSchema, {
            signature,
            inputs,
          }),
          signal,
        },
      })
    },

    /**
     * Address pool upload protocol (Stage 5a/5b).
     *
     * These three methods map directly to the begin/chunk/commit backend routes.
     * They are grouped here so `runPoolUpload` can receive the whole sub-namespace
     * as its `client` dependency.
     */
    addressPool: {
      begin: async (body: PoolUploadBeginInputs): Promise<PoolUploadBeginResponse> => {
        return await doFetch<PoolUploadBeginResponse>({
          endpoint: '/api/v1/admin/address-pool/begin',
          options: {
            method: 'POST',
            body: JSON.stringify(body),
          },
        })
      },

      chunk: async ({
        sessionId,
        chunkIndex,
        addresses,
      }: {
        sessionId: string
        chunkIndex: number
        addresses: AddressSetEntry[]
      }): Promise<PoolUploadChunkResponse> => {
        return await doFetch<PoolUploadChunkResponse>({
          endpoint: '/api/v1/admin/address-pool/chunk',
          options: {
            method: 'POST',
            body: JSON.stringify({ sessionId, chunkIndex, addresses }),
          },
        })
      },

      commit: async ({ sessionId }: { sessionId: string }): Promise<PoolUploadCommitResponse> => {
        return await doFetch<PoolUploadCommitResponse>({
          endpoint: '/api/v1/admin/address-pool/commit',
          options: {
            method: 'POST',
            body: JSON.stringify({ sessionId }),
          },
        })
      },
    },
  },

  // Markets endpoints
  markets: {
    // Get all available markets organized by exchange
    getAll: async ({ signal }: { signal: AbortSignal | undefined }) => {
      return await doFetch<MarketsResponse>({
        endpoint: '/api/v1/markets',
        options: { signal },
      })
    },

    // Get prices for multiple exchange+market pairs
    getPrices: async ({
      pairs,
      signal,
    }: {
      pairs: MarketPricePair[]
      signal: AbortSignal | undefined
    }) => {
      return await doFetch<MarketPriceResult[]>({
        endpoint: '/api/v1/markets/prices',
        options: {
          method: 'POST',
          body: serializeRequest(MarketPriceRequestSchema, pairs),
          signal,
        },
      })
    },
  },

  // Referral endpoints
  referral: {
    // Submit a new referral
    submit: async ({
      signature,
      inputs,
      signal,
    }: {
      signature: Hex
      inputs: ReferralSubmissionInputs
      signal: AbortSignal | undefined
    }) => {
      return await doFetch<ReferralSubmissionResponse>({
        endpoint: '/api/v1/referral',
        options: {
          method: 'POST',
          body: serializeRequestWithInputs(ReferralSubmissionRequestSchema, { signature, inputs }),
          signal,
        },
      })
    },

    // Disconnect a referral by setting endedAt
    disconnect: async ({
      signature,
      inputs,
      signal,
    }: {
      signature: Hex
      inputs: ReferralSubmissionInputs
      signal: AbortSignal | undefined
    }) => {
      return await doFetch<ReferralSubmissionResponse>({
        endpoint: '/api/v1/referral/disconnect',
        options: {
          method: 'POST',
          body: serializeRequestWithInputs(ReferralSubmissionRequestSchema, { signature, inputs }),
          signal,
        },
      })
    },

    // Get all referrals for an address
    getByAddress: async ({
      projectId,
      address,
      signal,
    }: {
      projectId: string
      address: string
      signal: AbortSignal | undefined
    }) => {
      return await doFetch<ReferralListResponse>({
        endpoint: `/api/v1/referral/${projectId}/${address}`,
        options: { signal },
      })
    },

    // Get active referrals for an address
    getActive: async ({
      projectId,
      address,
      signal,
    }: {
      projectId: string
      address: string
      signal: AbortSignal | undefined
    }) => {
      return await doFetch<ReferralListResponse>({
        endpoint: `/api/v1/referral/${projectId}/${address}/active`,
        options: { signal },
      })
    },
  },

  // Instance-admin endpoints (not bound to any campaign)
  instance: {
    whoami: async ({
      request,
      signal,
    }: {
      request: InstanceAuthRequest
      signal: AbortSignal | undefined
    }) => {
      return await doFetch<InstanceWhoAmIResponse>({
        endpoint: '/api/v1/admin/instance/whoami',
        options: {
          method: 'POST',
          body: serializeRequestWithInputs(InstanceAuthRequestSchema, request),
          signal,
        },
      })
    },
    labels: {
      list: async ({
        request,
        signal,
      }: {
        request: InstanceAuthRequest
        signal: AbortSignal | undefined
      }) =>
        doFetch<IdentityLabelListResponse>({
          endpoint: '/api/v1/admin/instance/labels',
          options: {
            method: 'POST',
            body: serializeRequestWithInputs(InstanceAuthRequestSchema, request),
            signal,
          },
        }),
      add: async ({
        request,
        signal,
      }: {
        request: IdentityLabelMutationRequest
        signal: AbortSignal | undefined
      }) =>
        doFetch<IdentityLabelListResponse>({
          endpoint: '/api/v1/admin/instance/labels/add',
          options: {
            method: 'POST',
            body: serializeRequestWithInputs(IdentityLabelMutationRequestSchema, request),
            signal,
          },
        }),
      remove: async ({
        request,
        signal,
      }: {
        request: IdentityLabelMutationRequest
        signal: AbortSignal | undefined
      }) =>
        doFetch<IdentityLabelListResponse>({
          endpoint: '/api/v1/admin/instance/labels/remove',
          options: {
            method: 'POST',
            body: serializeRequestWithInputs(IdentityLabelMutationRequestSchema, request),
            signal,
          },
        }),
    },
    activity: async ({
      request,
      signal,
    }: {
      request: InstanceActivityRequest
      signal: AbortSignal | undefined
    }) =>
      doFetch<InstanceActivityResponse>({
        endpoint: '/api/v1/admin/instance/activity',
        options: {
          method: 'POST',
          body: serializeRequestWithInputs(InstanceActivityRequestSchema, request),
          signal,
        },
      }),
    campaigns: async ({
      request,
      signal,
    }: {
      request: InstanceCampaignsRequest
      signal: AbortSignal | undefined
    }) =>
      doFetch<InstanceCampaignsResponse>({
        endpoint: '/api/v1/admin/instance/campaigns',
        options: {
          method: 'POST',
          body: serializeRequestWithInputs(InstanceCampaignsRequestSchema, request),
          signal,
        },
      }),
    identities: async ({
      request,
      signal,
    }: {
      request: InstanceIdentitiesRequest
      signal: AbortSignal | undefined
    }) =>
      doFetch<InstanceIdentitiesResponse>({
        endpoint: '/api/v1/admin/instance/identities',
        options: {
          method: 'POST',
          body: serializeRequestWithInputs(InstanceIdentitiesRequestSchema, request),
          signal,
        },
      }),
    cycleStatus: async ({
      request,
      signal,
    }: {
      request: InstanceCycleStatusRequest
      signal: AbortSignal | undefined
    }) =>
      doFetch<InstanceCycleStatusResponse>({
        endpoint: '/api/v1/admin/instance/cycle-status',
        options: {
          method: 'POST',
          body: serializeRequestWithInputs(InstanceCycleStatusRequestSchema, request),
          signal,
        },
      }),
    /**
     * Build the EventSource URL for the live-tail. EventSource cannot send a body
     * or headers, so the instance-login signature and the label filter ride in the
     * query string.
     */
    activityStreamUrl: ({
      signature,
      timestamp,
      filter,
    }: {
      signature: string
      timestamp: bigint
      filter: LabelFilter | undefined
    }): string => {
      const params = new URLSearchParams({ signature, timestamp: timestamp.toString() })
      if (filter && filter.labels.length > 0) {
        params.set('mode', filter.mode)
        params.set('labels', filter.labels.join(','))
      }
      return `${SACRIFICE_INDEXER_URL}/api/v1/admin/instance/activity/stream?${params.toString()}`
    },
  },

  // Utility endpoints
  utils: {
    // Convert Tron address to EVM hex format
    convertTronAddress: async ({
      address,
      signal,
    }: {
      address: string
      signal: AbortSignal | undefined
    }) => {
      const params = new URLSearchParams()
      params.append('address', address)
      return await doFetch<{ tronAddress: string; hexAddress: string }>({
        endpoint: `/api/v1/utils/convert-tron-address?${params.toString()}`,
        options: { signal },
      })
    },
  },
}
